import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiDelete } from '@mdi/js';
import { CircuitTemplate } from '../../types';
import { apiService } from '../../services/apiService';
import { DataTable, Column } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { adminTheme } from '../styles/adminTheme';

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
  margin-bottom: 16px;
`;

const Toggle = styled.button<{ $active: boolean }>`
  width: 38px;
  height: 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  position: relative;
  background: ${p => p.$active ? adminTheme.colors.primary : '#dadce0'};
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.$active ? '20px' : '2px'};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${adminTheme.colors.danger};

  &:hover {
    background: #fce8e6;
  }
`;

export const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<CircuitTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<CircuitTemplate | null>(null);
  const { toast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    apiService.getTemplates()
      .then(setTemplates)
      .catch(() => toast('Failed to load templates', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (template: CircuitTemplate) => {
    try {
      await apiService.updateTemplate(template.id, { is_active: !template.is_active });
      toast(`Template ${!template.is_active ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch {
      toast('Failed to update template', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteTemplate(deleteTarget.id);
      toast('Template deleted', 'success');
      setDeleteTarget(null);
      load();
    } catch {
      toast('Failed to delete template', 'error');
    }
  };

  const columns: Column<CircuitTemplate>[] = [
    { key: 'name', header: 'Name', sortable: true },
    { key: 'description', header: 'Description' },
    {
      key: 'is_active',
      header: 'Active',
      render: (row) => (
        <div onClick={e => e.stopPropagation()}>
          <Toggle $active={row.is_active} onClick={() => handleToggle(row)} />
        </div>
      ),
    },
    {
      key: 'node_count',
      header: 'Nodes',
      render: (row) => <span>{row.schema?.nodes?.length ?? 0}</span>,
    },
    {
      key: 'updated_at',
      header: 'Updated',
      sortable: true,
      render: (row) => new Date(row.updated_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (row) => (
        <div onClick={e => e.stopPropagation()}>
          <ActionBtn title="Delete" onClick={() => setDeleteTarget(row)}>
            <Icon path={mdiDelete} size={0.6} />
          </ActionBtn>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageTitle>Templates</PageTitle>

      <DataTable
        columns={columns}
        data={templates}
        loading={loading}
        emptyMessage="No templates found"
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Template"
          message={`Delete "${deleteTarget.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
